import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ClipboardList, Plus, X, Loader2, Copy, Trash2, Pencil, EyeOff, ChevronLeft, Search, Users, BookmarkPlus, RotateCcw, FileStack, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import RegistrationIntakePanel from './RegistrationIntakePanel';
import ExtSyncCard from './ExtSyncCard';
import FormBuilder from './RegFormBuilder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/reg-builder`;
const ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_LABEL = { draft: 'draft', open: 'open', closed: 'closed', opening_soon: 'opening soon', closing_soon: 'closing soon', scheduled: 'scheduled' };

export const RegBuilderPanel = ({ orgId, isDark = true, userRole, startNew = false, onStartConsumed }) => {
  const [tab, setTab] = useState('open');
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);       // form being built
  const [intakeForm, setIntakeForm] = useState(null);  // form whose intake is open
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [programFilter, setProgramFilter] = useState('');       // program name
  const [accountFilter, setAccountFilter] = useState('');       // deposit account id | '__none__'
  const [chooser, setChooser] = useState(null);                 // {templateId?} — New Registration dialog
  const [templates, setTemplates] = useState([]);
  const [importable, setImportable] = useState({ pending: [] });

  const text = isDark ? 'text-white' : 'text-slate-900';
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';

  const [autoFell, setAutoFell] = useState(false);
  const loadTemplates = useCallback(async () => {
    if (!orgId) return;
    try { const r = await axios.get(`${API}/${orgId}/form-templates`); setTemplates(r.data.templates || []); }
    catch { setTemplates([]); }
    try { const r = await axios.get(`${API}/${orgId}/form-templates-import`); setImportable(r.data || { pending: [] }); }
    catch { setImportable({ pending: [] }); }
  }, [orgId]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const load = useCallback(async () => {
    if (!orgId || tab === 'templates') { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/${orgId}/forms?status=${tab}`);
      const list = r.data.forms || [];
      setForms(list);
      // Land on the most recent registration: if Open is empty on first arrival,
      // auto-switch to Closed so the newest registration is visible immediately.
      if (list.length === 0 && tab === 'open' && !autoFell) { setAutoFell(true); setTab('closed'); }
    } catch { setForms([]); } finally { setLoading(false); }
  }, [orgId, tab, autoFell]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${ROOT}/financials/${orgId}/accounts`)
      .then(r => setAccounts(r.data.accounts || [])).catch(() => setAccounts([]));
  }, [orgId]);

  const programName = (f) => (f.program && typeof f.program === 'object' ? f.program.name : f.program) || '';
  const acctName = (id) => (accounts.find(a => a.id === id) || {}).name || 'No deposit account';

  const createForm = (newProgram = false) => {
    // Open a BLANK builder client-side; the form is POSTed on first Next/Save
    // once name + program are set (create requires a program object).
    setEditing({
      name: '', program: null, private: false, staff_only: false,
      open_date: '', close_date: '', welcome_message: '', confirmation_message: '',
      groups: [], questions: [], sections: [], agreements: [],
      financials: { deposit_account_id: null, pass_online_fee: true, enable_cash: true, installment_plans: [], discounts: [] },
      division_ids: [], status: 'draft', _newProgram: newProgram,
    });
  };

  // "+ Create Program" from the Programs tab opens New Registration in new-program mode.
  useEffect(() => {
    if (startNew) { createForm(true); onStartConsumed?.(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startNew]);
  const duplicate = async (f) => {
    try { await axios.post(`${API}/${orgId}/forms/${f.id}/duplicate`); toast.success('Duplicated'); load(); }
    catch { toast.error('Could not duplicate'); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Close "${f.name}"? Families can no longer register. You can reopen it later.`)) return;
    try { await axios.delete(`${API}/${orgId}/forms/${f.id}`); toast.success('Closed'); load(); }
    catch { toast.error('Could not close'); }
  };
  const reopen = async (f) => {
    try { await axios.patch(`${API}/${orgId}/forms/${f.id}`, { status: 'open' }); toast.success('Reopened'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not reopen'); }
  };
  const hardDelete = async (f) => {
    const typed = window.prompt(`Permanently delete "${f.name}"? Only registrations with no entries can be deleted.\nType Delete to confirm.`);
    if (typed !== 'Delete') return;
    try { await axios.delete(`${API}/${orgId}/forms/${f.id}?hard=true`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };
  const saveTemplate = async (f) => {
    const name = window.prompt('Template name', f.name);
    if (!name || !name.trim()) return;
    try { await axios.post(`${API}/${orgId}/forms/${f.id}/save-as-template`, { name: name.trim() }); toast.success('Saved as template'); loadTemplates(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save template'); }
  };
  const renameTemplate = async (t) => {
    const name = window.prompt('Template name', t.name);
    if (!name || !name.trim() || name.trim() === t.name) return;
    try { await axios.patch(`${API}/${orgId}/form-templates/${t.id}`, { name: name.trim() }); loadTemplates(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not rename'); }
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Registrations already made from it are not affected.`)) return;
    try { await axios.delete(`${API}/${orgId}/form-templates/${t.id}`); toast.success('Template deleted'); loadTemplates(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };
  const importPast = async () => {
    try {
      const r = await axios.post(`${API}/${orgId}/form-templates-import`);
      toast.success(`Imported ${r.data.templates_added?.length || 0} past registrations as templates`);
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
  };
  const copyLink = (f) => {
    const url = `${window.location.origin}/register/${orgId}/${f.public_id || f.id}`;
    navigator.clipboard?.writeText(url); toast.success('Public link copied');
  };

  const programs = [...new Set(forms.map(programName).filter(Boolean))].sort();
  const acctIds = [...new Set(forms.map(f => f.financials?.deposit_account_id || '__none__'))];
  const visible = forms.filter(f => {
    const q = search.trim().toLowerCase();
    if (q && !(f.name?.toLowerCase().includes(q) || programName(f).toLowerCase().includes(q))) return false;
    if (programFilter && programName(f) !== programFilter) return false;
    if (accountFilter && (f.financials?.deposit_account_id || '__none__') !== accountFilter) return false;
    return true;
  });

  const chip = (active, label, onClick, key, testid) => (
    <button key={key} onClick={onClick} data-testid={testid}
      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active
        ? 'bg-amber-600 border-amber-500 text-white'
        : isDark ? 'border-slate-600 text-slate-300 hover:border-amber-500' : 'border-slate-300 text-slate-600 hover:border-amber-500'}`}>
      {label}
    </button>
  );

  if (editing) return <FormBuilder orgId={orgId} form={editing} isDark={isDark} onExit={() => { setEditing(null); load(); }} />;

  if (intakeForm) return (
    <div className="space-y-3" data-testid="reg-intake-wrap">
      <div className="flex items-center gap-3">
        <button onClick={() => setIntakeForm(null)} className={`flex items-center gap-1 text-xs ${sub} hover:text-amber-400`} data-testid="intake-back">
          <ChevronLeft className="h-4 w-4" /> Back to registrations
        </button>
        <span className={`text-sm font-semibold ${text}`}>{intakeForm.name}</span>
      </div>
      <RegistrationIntakePanel orgId={orgId} initialFid={intakeForm.id} userRole={userRole} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="reg-builder-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className={`text-lg font-bold ${text} flex items-center gap-2`}><ClipboardList className="h-5 w-5 text-amber-500" /> Registrations</h2>
          <p className={`text-sm ${sub}`}>Build and manage registration forms, groups, questions and fees.</p>
        </div>
        <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={() => setChooser({})} data-testid="new-registration">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Registration
        </Button>
      </div>

      <div className="flex gap-1 border-b border-slate-700">
        {['open', 'draft', 'closed', 'templates'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`reg-tab-${t}`}
            className={`px-3 py-1.5 text-xs capitalize ${tab === t ? 'border-b-2 border-amber-500 text-amber-400' : sub}`}>{t}{t === 'templates' && templates.length ? ` (${templates.length})` : ''}</button>
        ))}
      </div>

      {tab !== 'templates' && <SoonSetting orgId={orgId} sub={sub} onSaved={load} />}
      {tab !== 'templates' && <ExtSyncCard orgId={orgId} isDark={isDark} onOpenForm={async (fid) => {
        try { const r = await axios.get(`${API}/${orgId}/forms/${fid}`); setIntakeForm(r.data.form); } catch { toast.error('Shadow form not found'); } }} />}
      {chooser && <NewRegistrationDialog orgId={orgId} isDark={isDark} templates={templates} initialTemplateId={chooser.templateId}
        onClose={() => setChooser(null)}
        onBlank={() => { setChooser(null); createForm(false); }}
        onCreated={(f) => { setChooser(null); setEditing(f); }} />}

      {tab === 'templates' ? (
        <TemplatesView templates={templates} importable={importable} isDark={isDark} card={card} text={text} sub={sub}
          onUse={(t) => setChooser({ templateId: t.id })} onRename={renameTemplate} onDelete={deleteTemplate} onImport={importPast} />
      ) : (<>

      <div className="space-y-2">
        <div className="relative max-w-xs">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input className={`h-8 text-sm pl-7 ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`} placeholder="Search registration or program…"
            value={search} onChange={e => setSearch(e.target.value)} data-testid="reg-search" />
        </div>
        {programs.length > 0 && (
          <div className="flex flex-wrap items-center gap-1" data-testid="reg-program-chips">
            <span className={`text-[10px] uppercase mr-1 ${sub}`}>Program</span>
            {chip(!programFilter, 'All', () => setProgramFilter(''), 'prog-all', 'chip-program-all')}
            {programs.map(p => chip(programFilter === p, p, () => setProgramFilter(programFilter === p ? '' : p), p, `chip-program-${p}`))}
          </div>
        )}
        {acctIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-1" data-testid="reg-account-chips">
            <span className={`text-[10px] uppercase mr-1 ${sub}`}>Deposit account</span>
            {chip(!accountFilter, 'All', () => setAccountFilter(''), 'acct-all', 'chip-account-all')}
            {acctIds.map(id => chip(accountFilter === id, acctName(id === '__none__' ? null : id),
              () => setAccountFilter(accountFilter === id ? '' : id), id, `chip-account-${id}`))}
          </div>
        )}
      </div>

      {loading ? (
        <div className={`flex items-center gap-2 ${sub} py-10 justify-center`}><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : visible.length === 0 ? (
        <div className={`border rounded-lg p-8 text-center ${card}`}><p className={sub}>No {tab} registrations{(search || programFilter || accountFilter) ? ' match your filters' : ''}.</p></div>
      ) : (
        <div className={`border rounded-lg overflow-x-auto ${card}`}>
          <table className="w-full text-xs">
            <thead className={sub}><tr className="text-left border-b border-slate-700">
              <th className="px-3 py-2">Name</th><th className="px-3 py-2">Registered</th><th className="px-3 py-2">Collected</th>
              <th className="px-3 py-2">Open</th><th className="px-3 py-2">Close</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {visible.map(f => (
                <tr key={f.id} className={`border-b border-slate-800 ${text} hover:bg-slate-800/40`} data-testid={`reg-row-${f.id}`}>
                  <td className="px-3 py-2 font-medium cursor-pointer" onClick={() => setIntakeForm(f)}
                    title="Open registrations (intake)" data-testid={`reg-open-intake-${f.id}`}>
                    {f.private && <EyeOff className="h-3 w-3 inline mr-1 text-slate-500" />}{f.name}
                    {programName(f) && <span className={`block text-[10px] ${sub}`}>{programName(f)}</span>}
                  </td>
                  <td className="px-3 py-2">{f.registered_count || f.external_summary?.registered || 0}</td>
                  <td className="px-3 py-2">{money(f.collected || f.external_summary?.collected || 0)}</td>
                  <td className="px-3 py-2">{(f.open_date || '—').slice(0, 10)}</td>
                  <td className="px-3 py-2">{(f.close_date || '—').slice(0, 10)}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] ${f.status === 'open' ? (String(f.display_status || '').endsWith('soon') ? 'bg-amber-900 text-amber-300' : 'bg-emerald-900 text-emerald-300') : f.status === 'draft' ? 'bg-sky-900 text-sky-300' : 'bg-slate-700 text-slate-300'}`} data-testid={`reg-status-${f.id}`}>{STATUS_LABEL[f.display_status || f.status] || f.status}</span></td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2 text-slate-400">
                      <button onClick={() => setIntakeForm(f)} title="Registrations" data-testid={`reg-intake-${f.id}`} className="hover:text-emerald-400"><Users className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setEditing(f)} title="Edit" data-testid={`reg-edit-${f.id}`} className="hover:text-amber-400"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => duplicate(f)} title="Duplicate" data-testid={`reg-duplicate-${f.id}`} className="hover:text-blue-400"><Copy className="h-3.5 w-3.5" /></button>
                      <button onClick={() => saveTemplate(f)} title="Save as template" data-testid={`reg-save-template-${f.id}`} className="hover:text-violet-400"><BookmarkPlus className="h-3.5 w-3.5" /></button>
                      <button onClick={() => copyLink(f)} title="Copy public link" data-testid={`reg-copylink-${f.id}`} className="hover:text-cyan-400"><Copy className="h-3.5 w-3.5 rotate-90" /></button>
                      {f.status === 'open'
                        ? <button onClick={() => remove(f)} title="Close" data-testid={`reg-close-${f.id}`} className="hover:text-orange-400"><X className="h-3.5 w-3.5" /></button>
                        : <button onClick={() => reopen(f)} title={f.status === 'draft' ? 'Open for registration' : 'Reopen'} data-testid={`reg-reopen-${f.id}`} className="hover:text-emerald-400"><RotateCcw className="h-3.5 w-3.5" /></button>}
                      {!(f.registered_count > 0) && <button onClick={() => hardDelete(f)} title="Delete (no entries)" data-testid={`reg-delete-${f.id}`} className="hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
};

// ── New Registration: blank or from a template ──────────────────────────────
// Copy a past registration's setup into a NEW season. The season list only shows
// the template's sport (current / upcoming); the default is the season after the
// template's, created on the fly when no program exists for it yet.
const seasonStart = (s) => { const m = /^(\d{4})/.exec(s || ''); return m ? parseInt(m[1], 10) : null; };
// Move the years in a name forward by `gap` ("… 2026-27" → "… 2027-28").
const rollYears = (txt, gap) => !gap ? txt : (txt || '').replace(/\b((?:19|20)\d{2})(?:([-/])(\d{2}|\d{4}))?\b/g, (_m, y, sep, y2) => {
  const ny = parseInt(y, 10) + gap;
  if (!sep) return String(ny);
  const n2 = y2.length === 4 ? String(parseInt(y2, 10) + gap) : String((parseInt(y2, 10) + gap) % 100).padStart(2, '0');
  return `${ny}${sep}${n2}`;
});

const NewRegistrationDialog = ({ orgId, isDark, templates, initialTemplateId, onClose, onBlank, onCreated }) => {
  const [mode, setMode] = useState(initialTemplateId ? 'template' : (templates.length ? 'template' : 'blank'));
  const [tid, setTid] = useState(initialTemplateId || '');
  const [programs, setPrograms] = useState([]);
  const [progId, setProgId] = useState('');
  const [name, setName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sports, setSports] = useState([]);
  const [np, setNp] = useState({ sport_id: '', year: '' });     // Other sport or season…
  const panel = isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-900';
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const sel = `h-8 text-sm rounded border w-full px-2 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`;
  useEffect(() => {
    axios.get(`${ROOT}/programs/${orgId}`).then(r => setPrograms(r.data.programs || [])).catch(() => setPrograms([]));
    axios.get(`${ROOT}/organizations/${orgId}/sports`).then(r => setSports(r.data || [])).catch(() => setSports([]));
  }, [orgId]);
  const t = templates.find(x => x.id === tid);
  const tYear = seasonStart(t?.season);
  const nextYear = tYear ? tYear + 1 : null;
  const tSport = sports.find(x => x.id === t?.sport_id);
  const sportName = t?.sport_name || tSport?.name || '';
  // Seasons offered: the template's sport only, current + upcoming (never past).
  const offered = programs
    .filter(p => t && p.sport_id === t.sport_id && p.status !== 'archived')
    .sort((x, y) => (x.season_year || 0) - (y.season_year || 0));
  const nextProg = offered.find(p => p.season_year === nextYear);
  const showNext = !!(t && nextYear && !nextProg && t.sport_id);
  const prog = programs.find(p => p.id === progId);
  const pickedYear = progId === '__next__' ? nextYear : progId === '__new__' ? parseInt(np.year, 10) || null : prog?.season_year ?? null;
  const gap = (tYear && pickedYear) ? pickedYear - tYear : 0;
  const sportsWithTemplates = [...new Set(templates.map(x => x.sport_name || 'All sports'))].sort();
  // Default season = the one after the template's.
  useEffect(() => {
    if (!t) return;
    setProgId(nextProg ? nextProg.id : (showNext ? '__next__' : (offered[0]?.id || '__new__')));
    if (!t.sport_id) setNp(v => ({ ...v, sport_id: '' }));
  }, [tid, programs.length, sports.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // Name follows the season until the AD types their own.
  useEffect(() => { if (t && !nameEdited) setName(rollYears(t.name, gap)); }, [tid, gap]); // eslint-disable-line react-hooks/exhaustive-deps
  const create = async () => {
    if (mode === 'blank') { onBlank(); return; }
    if (!t) { toast.error('Pick a registration to copy'); return; }
    let program = prog ? { sport_id: prog.sport_id, season: prog.season, name: prog.name, key: prog.id, sport: prog.sport } : null;
    if (progId === '__next__') {
      if (!t.sport_id) { toast.error('Pick the sport and season under "Other sport or season"'); return; }
      program = { sport_id: t.sport_id, season: `${nextYear}-${nextYear + 1}`, name: `${sportName} ${nextYear}-${nextYear + 1}` };
    }
    if (progId === '__new__') {
      const y = parseInt(np.year, 10);
      const sp = sports.find(x => x.id === np.sport_id);
      if (!sp || !y) { toast.error('Pick the sport and the season start year'); return; }
      program = { sport_id: sp.id, season: `${y}-${y + 1}`, name: `${sp.name} ${y}-${y + 1}` };
    }
    if (!program) { toast.error('Pick the season this registration is for'); return; }
    if (!name.trim()) { toast.error('Registration name is required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${orgId}/form-templates/${t.id}/create-form`, {
        name: name.trim(), program,
      });
      toast.success('Registration created as a draft — review each step, then open it');
      onCreated(r.data.form);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="new-reg-dialog">
      <div className={`border rounded-lg p-4 w-full max-w-lg space-y-3 ${panel}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-bold">New registration</h3><button onClick={onClose}><X className="h-4 w-4" /></button></div>
        <div className="flex gap-2">
          {[['template', 'Copy a past registration'], ['blank', 'Start blank']].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`new-reg-mode-${k}`}
              className={`flex-1 text-xs rounded border px-3 py-2 ${mode === k ? 'border-amber-500 bg-amber-600/20 text-amber-400' : isDark ? 'border-slate-600' : 'border-slate-300'}`}>{l}</button>
          ))}
        </div>
        {mode === 'template' && (
          <div className="space-y-3">
            {templates.length === 0 && <p className={`text-xs ${sub}`}>No templates yet. Save any registration as a template from its row, or import past registrations under the Templates tab.</p>}
            {templates.length > 0 && (
              <div><label className="text-xs font-semibold">1. Copy the setup from</label>
                <p className={`text-[11px] ${sub} mb-1`}>Its groups, questions, waivers, fees and payment plans are copied. The past registration itself is not changed.</p>
                <select className={sel} value={tid} onChange={e => { setTid(e.target.value); setNameEdited(false); }} data-testid="new-reg-template">
                  <option value="">Select a past registration…</option>
                  {sportsWithTemplates.map(sp => (
                    <optgroup key={sp} label={sp}>
                      {templates.filter(x => (x.sport_name || 'All sports') === sp).map(x => <option key={x.id} value={x.id}>{x.name}{x.season ? ` (${x.season})` : ''}</option>)}
                    </optgroup>
                  ))}
                </select>
                {t && <p className={`text-[11px] ${sub} mt-1`} data-testid="new-reg-template-summary">{t.group_count} groups · {t.question_count} questions · {t.agreement_count} waivers · {t.plan_count} payment plans · {t.discount_count} discounts</p>}
              </div>
            )}
            {t && (
              <div><label className="text-xs font-semibold">2. Season this new registration is for</label>
                <p className={`text-[11px] ${sub} mb-1`}>Families sign up for this season. Birth-date cut-offs and dates move forward from {t.season || 'the template'} to the season you pick.</p>
                <select className={sel} value={progId} onChange={e => setProgId(e.target.value)} data-testid="new-reg-program">
                  {showNext && <option value="__next__">{sportName} {nextYear}-{nextYear + 1} — next season (new)</option>}
                  {offered.map(p => <option key={p.id} value={p.id}>{(p.name || '').includes(p.season) ? p.name : `${p.name} (${p.season})`}{p.season_year === nextYear ? ' — next season' : p.season_year === tYear ? ' — same season as the template' : ''}</option>)}
                  <option value="__new__">Other sport or season…</option>
                </select>
                {progId === '__new__' && (
                  <div className="flex gap-2 mt-2" data-testid="new-reg-new-program">
                    <select className={sel} value={np.sport_id} onChange={e => setNp(v => ({ ...v, sport_id: e.target.value }))} data-testid="new-reg-np-sport">
                      <option value="">Sport…</option>
                      {sports.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                    <Input type="number" className={`${sel} w-40`} placeholder="Season start year" value={np.year} onChange={e => setNp(v => ({ ...v, year: e.target.value }))} data-testid="new-reg-np-year" />
                  </div>
                )}
                {pickedYear && tYear && pickedYear <= tYear && (
                  <p className="text-[11px] text-amber-500 mt-1" data-testid="new-reg-same-season">This is {pickedYear === tYear ? 'the same season as' : 'earlier than'} the registration you are copying — dates will not move forward.</p>
                )}
              </div>
            )}
            {t && (
              <div><label className="text-xs font-semibold">3. Name families will see</label>
                <Input className={sel} value={name} onChange={e => { setName(e.target.value); setNameEdited(true); }} data-testid="new-reg-name" /></div>
            )}
            {t && <p className={`text-[11px] ${sub}`}>Created as a draft — nothing is public until you open it.</p>}
          </div>
        )}
        {mode === 'blank' && <p className={`text-xs ${sub}`}>Opens the builder with an empty registration.</p>}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={create} data-testid="new-reg-create">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Create draft'}</Button>
        </div>
      </div>
    </div>
  );
};

// ── Templates tab ───────────────────────────────────────────────────────────
const TemplatesView = ({ templates, importable, isDark, card, text, sub, onUse, onRename, onDelete, onImport }) => {
  const pending = importable?.pending || [];
  const sports = [...new Set(templates.map(x => x.sport_name || 'All sports'))].sort();
  return (
    <div className="space-y-3" data-testid="reg-templates">
      <p className={`text-xs ${sub}`}>A template is a saved registration setup — groups, questions, agreements, payment plans and discounts. Use one to start next season's registration.</p>
      {pending.length > 0 && (
        <div className={`border rounded-lg p-3 flex items-center gap-3 flex-wrap ${isDark ? 'border-amber-700 bg-amber-900/20' : 'border-amber-300 bg-amber-50'}`} data-testid="templates-import-banner">
          <FileStack className="h-4 w-4 text-amber-500" />
          <span className={`text-xs ${text}`}>{pending.length} past registration{pending.length === 1 ? '' : 's'} ready to import as templates: {pending.map(x => x.name).join(' · ')}</span>
          <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700 ml-auto" onClick={onImport} data-testid="templates-import"><Download className="h-3.5 w-3.5 mr-1" /> Import</Button>
        </div>
      )}
      {templates.length === 0 ? (
        <div className={`border rounded-lg p-8 text-center ${card}`}><p className={sub}>No templates yet.</p></div>
      ) : sports.map(sp => (
        <div key={sp} className={`border rounded-lg overflow-x-auto ${card}`}>
          <div className={`px-3 py-2 text-xs font-semibold ${text} border-b border-slate-700`}>{sp}</div>
          <table className="w-full text-xs">
            <thead className={sub}><tr className="text-left border-b border-slate-700">
              <th className="px-3 py-2">Template</th><th className="px-3 py-2">Season</th><th className="px-3 py-2">Groups</th><th className="px-3 py-2">Questions</th><th className="px-3 py-2">Agreements</th><th className="px-3 py-2">Plans / discounts</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {templates.filter(x => (x.sport_name || 'All sports') === sp).map(t => (
                <tr key={t.id} className={`border-b border-slate-800 ${text}`} data-testid={`template-row-${t.id}`}>
                  <td className="px-3 py-2 font-medium">{t.name}<span className={`block text-[10px] ${sub}`}>{t.source === 'import' ? 'Past registration' : 'Saved from a registration'}</span></td>
                  <td className="px-3 py-2">{t.season || '—'}</td>
                  <td className="px-3 py-2">{t.group_count}</td>
                  <td className="px-3 py-2">{t.question_count}</td>
                  <td className="px-3 py-2">{t.agreement_count}</td>
                  <td className="px-3 py-2">{t.plan_count} / {t.discount_count}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2 text-slate-400">
                      <button onClick={() => onUse(t)} className="text-amber-400 hover:underline" data-testid={`template-use-${t.id}`}>Use</button>
                      <button onClick={() => onRename(t)} title="Rename" className="hover:text-amber-400"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => onDelete(t)} title="Delete" className="hover:text-red-400" data-testid={`template-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

// ── The 5-step builder ────────────────────────────────────────────────────

export default RegBuilderPanel;


// R6 — how many days before open/close a registration shows Opening soon / Closing soon.
function SoonSetting({ orgId, sub, onSaved }) {
  const [v, setV] = useState('');
  useEffect(() => {
    axios.get(`${ROOT}/organizations/${orgId}`).then(r => setV(r.data?.settings?.registration_soon_days ?? '')).catch(() => {});
  }, [orgId]);
  const save = async () => {
    try { await axios.put(`${ROOT}/organizations/${orgId}`, { settings: { registration_soon_days: v === '' ? null : parseInt(v, 10) } }); toast.success('Saved'); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  return (
    <div className={`flex items-center gap-2 text-[11px] ${sub}`} data-testid="soon-setting">
      Show "Opening soon" / "Closing soon" within
      <input type="number" min="0" max="60" value={v} placeholder="off" onChange={e => setV(e.target.value)} onBlur={save}
        className="w-14 h-6 rounded border px-1 bg-transparent border-slate-600" data-testid="soon-days" /> days
    </div>
  );
}

